import React, { useEffect } from 'react';
import { Dialog, DialogActionsProps, DialogProps, DialogTitleProps } from '@mui/material';
import { DraggablePaper } from './DraggablePaper';
import { Paper } from './Paper';
import { DragHandle } from './DragHandle';
import { CloseButton } from './CloseButton';
import { ModalContent } from './ModalContent';

/**
 * Props pour le composant Modalizer
 */
export type ModalizerProps = DialogProps & {
    /** État d'ouverture de la fenêtre modale */
    open: DialogProps['open'];
    /** Fonction appelée lors de la fermeture de la fenêtre modale */
    onClose: () => void;
    /** Contenu de la fenêtre modale */
    children: DialogProps['children'];
    /** Titre de la fenêtre modale */
    modalTitle?: DialogTitleProps['children'];
    /** Actions affichées en bas de la fenêtre modale */
    modalActions?: DialogActionsProps['children'];
    /** Active le déplacement de la fenêtre modale */
    isDraggable?: boolean;
    /** Permet à la fenêtre modale d'occuper toute la largeur disponible */
    fullWidth?: boolean;
};

/**
 * Fenêtre modale avec support de déplacement, titre et actions personnalisés.
 *
 * Ce composant encapsule un Dialog MUI avec des fonctionnalités supplémentaires :
 * - Une poignée de déplacement optionnelle
 * - Un titre personnalisé
 * - Des actions personnalisées en pied de modal
 * - Une barre de défilement intégrée
 */
export const Modalizer: React.FunctionComponent<ModalizerProps> = function ({
    open,
    onClose,
    modalActions,
    modalTitle,
    children,
    isDraggable = false,
    fullWidth = false,
    ...props
}: ModalizerProps) {
    // Centre la modal lorsqu'elle s'ouvre
    useEffect(() => {
        if (open && isDraggable) {
            setTimeout(() => {
                const dragElement = document.getElementById('draggable-dialog-title');
                if (dragElement) {
                    const parent = dragElement.closest('[role="dialog"]');
                    if (parent) {
                        // Réinitialiser la position à zéro (centrée)
                        (parent as HTMLElement).style.transform = '';
                    }
                }
            }, 100); // Petit délai pour s'assurer que le DOM est mis à jour
        }
    }, [open, isDraggable]);

    // Sélectionne le bon PaperComponent en fonction de isDraggable
    const SelectedPaperComponent = isDraggable ? DraggablePaper : Paper;

    return (
        <Dialog
            fullWidth={fullWidth}
            open={open}
            onClose={onClose}
            PaperComponent={SelectedPaperComponent}
            sx={{ margin: 'auto', maxWidth: '3000px' }}
            {...props}
        >
            {isDraggable && <DragHandle />}
            {onClose && <CloseButton onClose={onClose} />}
            <ModalContent
                modalTitle={modalTitle}
                modalActions={modalActions}
            >
                {children}
            </ModalContent>
        </Dialog>
    );
};
